import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useSite } from '../context/SiteContext';
import { useCart } from '../context/CartContext';
import { Minus, Plus, ChevronDown, ChevronUp, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import CommitmentSection from '../components/CommitmentSection';
import { optimizeImageUrl } from '../utils/imageOptimizer';

const ProductPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { siteData } = useSite();
  const { addToCart } = useCart();
  
  const product = siteData.products.find(p => p.slug === slug);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [openFaq, setOpenFaq] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Set initial variant when product loads
  React.useEffect(() => {
    if (product?.variants?.[0] && !selectedVariant) {
      setSelectedVariant(product.variants[0]);
    }
  }, [product, selectedVariant]);

  // Reset selected image when product changes
  React.useEffect(() => {
    setSelectedImageIndex(0);
  }, [slug]);

  // Generate FAQ schema for SEO
  const faqSchema = product?.faqs && product.faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": product.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  // Generate Breadcrumb schema for SEO
  const breadcrumbSchema = product ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.edenremedies.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Products",
        "item": "https://www.edenremedies.com/products"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": product.name,
        "item": `https://www.edenremedies.com/products/${product.slug}`
      }
    ]
  } : null;

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-serif mb-4">Product not found</h1>
          <Button onClick={() => navigate('/products')} className="bg-[#2d4a47] hover:bg-[#1e3633] rounded-none">
            Back to Shop
          </Button>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product, selectedVariant, quantity);
  };

  return (
    <div className="min-h-screen py-12">
      {/* Schema Markup for SEO */}
      <Helmet>
        {faqSchema && (
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        )}
        {breadcrumbSchema && (
          <script type="application/ld+json">
            {JSON.stringify(breadcrumbSchema)}
          </script>
        )}
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-[#2d4a47] transition-colors">Home</Link>
          <ChevronRight size={14} />
          <Link to="/products" className="hover:text-[#2d4a47] transition-colors">Products</Link>
          <ChevronRight size={14} />
          <span className="text-gray-900">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images Gallery */}
          <div>
            {/* Main Image */}
            <div className="aspect-square bg-gray-50 mb-4">
              <img
                src={optimizeImageUrl(product.images?.[selectedImageIndex] || product.images?.[0], { width: 600 })}
                alt="Natural and organic skincare product"
                className="w-full h-full object-cover"
                width={600}
                height={600}
                fetchpriority="high"
              />
            </div>
            
            {/* Thumbnail Gallery */}
            {product.images && product.images.length > 1 && (
              <div className="grid grid-cols-4 gap-3">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`aspect-square bg-gray-50 overflow-hidden border-2 transition-all ${
                      selectedImageIndex === index 
                        ? 'border-[#2d4a47]' 
                        : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={optimizeImageUrl(image, { width: 150 })}
                      alt={`Product image ${index + 1}`}
                      className="w-full h-full object-cover"
                      width={150}
                      height={150}
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div>
            <h1 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4">
              {product.name}
            </h1>
            
            <div className="flex items-center gap-4 mb-6">
              <span className="text-2xl font-medium text-gray-900">
                ${(selectedVariant?.price ?? product.variants?.[0]?.price ?? 0).toFixed(2)}
              </span>
              {(selectedVariant?.comparePrice || product.variants?.[0]?.comparePrice) > 0 && 
               (selectedVariant?.comparePrice || product.variants?.[0]?.comparePrice) > (selectedVariant?.price || product.variants?.[0]?.price) && (
                <span className="text-lg text-gray-400 line-through">
                  ${(selectedVariant?.comparePrice || product.variants?.[0]?.comparePrice).toFixed(2)}
                </span>
              )}
              {product.onSale && (
                <span className="bg-red-100 text-red-700 text-xs px-2 py-1 uppercase tracking-wider">
                  Sale
                </span>
              )}
            </div>

            <p className="text-gray-600 leading-relaxed mb-8">
              {product.description}
            </p>

            {/* Size Selection */}
            {product.variants && product.variants.length > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-medium tracking-wider uppercase mb-3">Size</h3>
                <div className="flex gap-4">
                  {product.variants.map((variant) => (
                    <button
                      key={variant.id}
                      onClick={() => setSelectedVariant(variant)}
                      className={`px-6 py-3 border-2 transition-colors ${
                        selectedVariant?.id === variant.id
                          ? 'border-[#2d4a47] bg-[#2d4a47] text-white'
                          : 'border-gray-200 hover:border-[#2d4a47]'
                      }`}
                    >
                      {variant.size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mb-8">
              <h3 className="text-sm font-medium tracking-wider uppercase mb-3">Quantity</h3>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 border border-gray-300 flex items-center justify-center hover:border-[#2d4a47] transition-colors"
                >
                  <Minus size={16} />
                </button>
                <span className="text-lg font-medium w-10 text-center">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 border border-gray-300 flex items-center justify-center hover:border-[#2d4a47] transition-colors"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            {/* Add to Cart */}
            <Button
              onClick={handleAddToCart}
              className="w-full bg-[#2d4a47] hover:bg-[#1e3633] text-white rounded-none py-6 text-sm tracking-wider mb-8"
            >
              ADD TO CART - ${((selectedVariant?.price || 0) * quantity).toFixed(2)}
            </Button>
          </div>
        </div>

        {/* Product Description */}
        <div className="mt-16 pt-16 border-t">
          <h2 className="text-2xl font-serif mb-8">Product Details</h2>
          <div 
            className="prose prose-lg max-w-none text-gray-600"
            dangerouslySetInnerHTML={{ __html: product.longDescription }}
          />
        </div>

        {/* FAQ Section */}
        {product.faqs && product.faqs.length > 0 && (
          <div className="mt-16 pt-16 border-t">
            <h2 className="text-2xl font-serif mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4 max-w-3xl">
              {product.faqs.map((faq, index) => (
                <div 
                  key={index} 
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-gray-900 pr-4">{faq.question}</span>
                    {openFaq === index ? (
                      <ChevronUp size={20} className="text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-5 text-gray-600 leading-relaxed bg-gray-50">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Commitment Section */}
      <CommitmentSection variant="box" />
    </div>
  );
};

export default ProductPage;
